<template>
  <Dialog
    v-model:visible="showModalRef"
    :close-on-escape="false"
    :dismissableMask="store.getters.dialogsDismissableMask"
    :modal="store.getters.dialogsModal"
  >
    <template #header>
      {{ title }}
      <i
        class="custom-close-dialog-button cursor-pointer p-2 pi pi-times"
        @click="closeModal()"
      ></i>
    </template>

    <div
      :class="[loadingForm ? 'flex' : 'hidden']"
      class="justify-content-center align-items-center h-full"
    >
      <i class="pi pi-spinner pi-spin" style="font-size: 2rem"></i>
    </div>

    <Form
      ref="formRef"
      v-bind="formConfigRef"
      :class="[loadingForm ? 'hidden' : 'flex']"
    >
      <template #emailTitle__before>
        <div class="text-lg font-semibold">Correo electrónico</div>
      </template>

      <template #passwordTitle__before>
        <div class="text-lg font-semibold">Contraseña</div>
      </template>

      <template #generatePassword__before>
        <Button
          label="Generar contraseña segura"
          @click="handleGenerateRandomPassword"
        />
      </template>
    </Form>

    <template #footer>
      <div class="col-12">
        <div class="flex justify-content-between flex-wrap gap-3">
          <Button label="Cancelar" severity="secondary" @click="closeModal()" />
          <Button
            :label="title"
            @click="save"
            :loading="loading || loadingForm"
            loading-icon="pi pi-spinner"
          />
        </div>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useToast } from "primevue/usetoast";
import { useStore } from "vuex";
import { useConfirm } from "primevue/useconfirm";
import Dialog from "primevue/dialog";
import Button from "primevue/button";
import { Form, useImmer, useValidations } from "@sttedra_ui/framework";

import useFormConfig, {
  getGroups,
} from "@/pages/_root/users-dialog/users-dialog-form.view-config.js";
import {
  appUserNewInternalUser,
  appUserNewExternalUser,
  appUserUpdateInternalExternalUser,
} from "@/api/users/users.api.js";
import {
  createRandomPassword,
  checkFormHasData,
  handleShowConfirm,
} from "@/composables/_commons/helper.js";
import {
  convertUtcToLocalTime,
} from "@/composables/_commons/helper.js";

// #region Props, Emits
const emit = defineEmits(["saved"]);
// #endregion

// #region Params, Ref, Reactives, Computed
const loadingForm = ref(false);
const loading = ref(false);
const store = useStore();
const confirm = useConfirm();
const { required } = useValidations();
const toast = useToast();
const showModalRef = ref(false);
const formRef = ref(null);
const formConfig = ref(null);
const { ref: formConfigRef, update: updateFormConfig } = useImmer(formConfig);
const register = ref(null);
const isInternal = ref(false);

const title = computed(() => {
  return `${register.value?.idUser ? "Editar" : "Crear"} usuario ${
    isInternal.value ? "interno" : "externo"
  }`;
});
// #endregion

// #region Functions
async function showModal(data, type) {
  loadingForm.value = true;

  await getGroups();
  formConfig.value = new useFormConfig(null, type, false);

  showModalRef.value = true;
  register.value = null;
  isInternal.value = type === "ministerio";

  if (data != null) {
    if (data.idGrupo?.length == 0) {
      data.idGrupo = null;
    }

    if (data.habilitadoHasta) {
      data.habilitadoHasta = convertUtcToLocalTime(data.habilitadoHasta);
    }

    register.value = data;

    const object = Object.keys(data).map((key) => {
      return {
        name: key,
        value: data[key],
      };
    });

    formConfig.value = new useFormConfig(object, type, true);
  }

  loadingForm.value = false;
}

function loadEmailRules() {
  updateFormConfig((config) => {
    const items = config.items;

    items.forEach((item) => {
      if (item.field == "email") {
        item.rules = { required };
      }
    });

    return config;
  });
}

function handleResponse(response) {
  if (response == true) {
    toast.add({
      severity: "success",
      summary: `Registro ${register.value?.idUser ? "editado" : "creado"}`,
      detail: `${
        register.value?.idUser ? "Edición" : "Creación"
      } realizada correctamente`,
      life: 3000,
    });

    showModalRef.value = false;

    emit("saved");
  } else {
    const error = response.response?.data?.errors?.[0]?.message;

    if (!error) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: `No se ha
        podido realizar la ${register.value?.idUser ? "edición" : "creación"}`,
        life: 3000,
      });
    } else {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: error,
        life: 3000,
      });
    }
  }
}

async function save() {
  loadEmailRules();

  setTimeout(async () => {
    const invalid = !(await formRef.value?.v$Ref.$validate());

    if (invalid) return;

    const data = { ...formRef.value.state$$.values };

    if (
      data.email != data.ConfirmEmail &&
      data.ConfirmEmail &&
      isInternal.value
    ) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Los correos electrónicos no coinciden",
        life: 3000,
      });

      return;
    }

    if (
      !isInternal.value &&
      data.Contrasena &&
      data.Contrasena != data.confirmContrasena
    ) {
      toast.add({
        severity: "error",
        summary: "Error",
        detail: "Las contraseñas no coinciden",
        life: 3000,
      });

      return;
    }

    loading.value = true;

    data.grupo = data.idGrupo;
    data.afiliacion = data.unidad;
    data.activo = !!formRef.value.state$$.values.activo;

    let response = null;

    if (register.value?.idUser) {
      data.id = register.value.idUser;
      data.EmailAntiguo = register.value.email;

      response = await appUserUpdateInternalExternalUser(data);
    } else {
      if (isInternal.value) {
        response = await appUserNewInternalUser(data);
      } else {
        response = await appUserNewExternalUser(data);
      }
    }

    handleResponse(response);

    loading.value = false;
  });
}

function handleGenerateRandomPassword() {
  const newPassword = createRandomPassword();

  formRef.value.state$$.values.Contrasena = newPassword;
  formRef.value.state$$.values.confirmContrasena = newPassword;
}

function closeModal() {
  let showConfirm = false;

  if (checkFormHasData({ ...formRef.value.state$$.values })) {
    showConfirm = true;
  }

  if (showConfirm) {
    handleShowConfirm(confirm, showModalRef);
  } else {
    showModalRef.value = false;
  }
}
// #endregion

// #region Effects, Hooks
watch(
  () => formRef.value?.state$$.values.EnviarEmail,
  (value) => {
    updateFormConfig((config) => {
      const items = config.items;

      items.forEach((item) => {
        if (item.field == "mensajeEmail") {
          item.properties = {
            disabled: !value,
          };

          if (value) {
            item.rules = { required };
          } else {
            item.rules = {};
          }
        }
      });

      return config;
    });
  },
);
// #endregion

// #region Provide, Expose
defineExpose({
  showModal,
});
// #endregion
</script>
